import React, { useCallback, useEffect, useState } from "react";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Grid from "@material-ui/core/Grid";
import { Box, Card } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import {
  append,
  find,
  isEmpty,
  map,
  mergeRight,
  pluck,
  propEq,
  uniq,
  without,
} from "ramda";
import { useSearchQuery } from "../common/use-searchQuery";
import { useSearch } from "../searchServices";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import { Link } from "react-router-dom";
import Button from "@material-ui/core/Button";
import AddIcon from "@material-ui/icons/Add";
import DotPagination from "./DotPagination";
import { anyTrue, notBlank } from "../../util/ext";
import Tooltip from "@material-ui/core/Tooltip";

import ContentGroupTypeSelect from "./ContentGroupTypeSelect";
import DateRangeSelect, {
  calculateDate,
  dateRanges,
} from "../../components/dashboard/DateRangeSelect";
import Image from "../../components/Image";
import NewContentGroupDialog from "../organize/content-groups/create-new-dialog/NewContentGroupDialog";
import {
  useCurrentOrgId,
  useIsAdmin,
  useIsUserIAPManager,
} from "../../auth/authUtils";

const IMAGE_DIMENSONS = {
  XS: 100,
  CREATOR_ADMIN_DASHBOARD_MD: 120,
  MD: 120,
  LG: 140,
};

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
    // minWidth: 735,
    // height: 385,
    height: 385,
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderRadius: 20,
    color: theme.palette.secondary.contrastText,
    display: "flex",
    justifyContent: "flex-start",
    flexDirection: "column",
    [theme.breakpoints.up("md")]: {
      height: 400,
    },
  },

  h1: {
    fontSize: "3.6rem",
    lineHeight: "3.4rem",
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },

  h4: {
    fontSize: "1.2rem",
    lineHeight: "1.8rem",
    textTransform: "uppercase",
    fontWeight: "normal",
  },

  grey: {
    color: theme.palette.secondary.contrastText,
  },

  body2: {
    fontSize: "1.4rem",
    lineHeight: "1.8rem",
  },

  addButton: {
    fontSize: "1.4rem",
    lineHeight: "1.8rem",
    fontWeight: "bold",
  },

  contentGroupList: {
    marginTop: 50,
  },

  contentGroupThumbnailWrapper: {
    width: IMAGE_DIMENSONS.XS,
    height: "auto",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up("creatorAdminDashboardMd")]: {
      width: IMAGE_DIMENSONS.CREATOR_ADMIN_DASHBOARD_MD,
    },
    [theme.breakpoints.up("md")]: {
      width: IMAGE_DIMENSONS.MD,
    },
    [theme.breakpoints.up("lg")]: {
      width: IMAGE_DIMENSONS.LG,
    },
  },

  contentGroupThumbnail: {
    width: IMAGE_DIMENSONS.XS,
    height: IMAGE_DIMENSONS.XS,
    borderRadius: 20,
    position: "relative",
    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.15)",
    backgroundColor: "#E5E5E5",
    [theme.breakpoints.up("creatorAdminDashboardMd")]: {
      width: IMAGE_DIMENSONS.CREATOR_ADMIN_DASHBOARD_MD,
      height: IMAGE_DIMENSONS.CREATOR_ADMIN_DASHBOARD_MD,
    },
    [theme.breakpoints.up("md")]: {
      width: IMAGE_DIMENSONS.MD,
      height: IMAGE_DIMENSONS.MD,
    },
    [theme.breakpoints.up("lg")]: {
      width: IMAGE_DIMENSONS.LG,
      height: IMAGE_DIMENSONS.LG,
    },
  },

  contentGroupItem: {
    width: IMAGE_DIMENSONS.XS,
    [theme.breakpoints.up("creatorAdminDashboardMd")]: {
      width: IMAGE_DIMENSONS.CREATOR_ADMIN_DASHBOARD_MD,
    },
    [theme.breakpoints.up("md")]: {
      width: IMAGE_DIMENSONS.MD,
    },
    [theme.breakpoints.up("lg")]: {
      width: IMAGE_DIMENSONS.LG,
    },
  },

  mediaType: {
    color: theme.palette.primary.brandPrimary,
    fontSize: "1.2rem",
    fontWeight: 700,
    lineHeight: "16px",
    textTransform: "uppercase",
  },

  progressWrapper: {
    flexGrow: 1,
  },

  body1: {
    fontStyle: "italic",
    fontSize: "1.4rem",
    lineHeight: "1.8rem",
    color: theme.palette.primary.main,
  },

  newButton: {
    fontSize: "1.2rem",
    lineHeight: "1.8rem",
    fontWeight: "bold",
    paddingTop: theme.spacing(2),
    color: theme.palette.primary.main,
  },

  forwardIcon: {
    color: theme.palette.primary.main,

    "& :first-child": {
      fontSize: "1.2rem",
      lineHeight: "1.8rem",
      fontWeight: "bold",
      color: theme.palette.primary.main,
    },
  },
}));

const initialSearchState = {
  appliedQuery: [],
  appliedFacets: [
    {
      name: "type",
      queryParam: "_type",
      terms: [
        { code: "ANTHOLOGY" },
        { code: "COLLECTION" },
        { code: "MODULE" },
      ],
    },
  ],
  orderBy: "recordDate",
  orderDirection: "desc",
  otherParams: [],
  appliedPublicationYearGroups: [],
  page: 0,
  limit: 6,
  noFacets: true,
};

const getEditUrl = contentGroup => {
  const mediaType =
    contentGroup.mediaType === "module"
      ? "modules"
      : contentGroup.mediaType === "anthology"
      ? "anthologies"
      : "collections";
  return `/creator/${mediaType}/${contentGroup.id}`;
};

const getSelectedTypes = appliedFacets => {
  const types = find(propEq("name", "type"), appliedFacets);
  return types ? pluck("code", types.terms) : [];
};

const ALL_TYPES = ["MODULE", "ANTHOLOGY", "COLLECTION"];

const ContentGroupsCard = ({ title, range, limit = 6 }) => {
  const classes = useStyles();
  const isCreatorAdminDashMd = useMediaQuery(theme =>
    theme.breakpoints.up("creatorAdminDashboardMd"),
  );
  const isMd = useMediaQuery(theme => theme.breakpoints.up("md"));
  const isLg = useMediaQuery(theme => theme.breakpoints.up("lg"));
  const currentOrgId = useCurrentOrgId();
  const isAdmin = useIsAdmin();
  const isIapManager = useIsUserIAPManager();

  //-- Handle Search state
  const initialSearchQuery = mergeRight(
    mergeRight(initialSearchState, { limit }),
    notBlank(range)
      ? { otherParams: [{ aa: `${calculateDate(range)}|` }] }
      : {},
  );

  const {
    state,
    handleUpdatePage,
    handleUpdateFacets,
    handleUpdateOtherParams,
    handleUpdateLimit,
  } = useSearchQuery(
    mergeRight(initialSearchQuery, {
      appliedFacets: isAdmin
        ? initialSearchQuery.appliedFacets
        : append(
            {
              name: "creatorOrgs",
              queryParam: "_co",
              terms: [{ code: currentOrgId }],
            },
            initialSearchQuery.appliedFacets,
          ),
    }),
  );

  useEffect(() => {
    if (state.limit !== limit) {
      handleUpdateLimit(limit);
    }
  }, [limit, state.limit, handleUpdateLimit]);

  const selectedTypes = getSelectedTypes(state.appliedFacets);

  const handleTypeChange = type => event => {
    event.stopPropagation();
    const types = event.target.checked
      ? uniq(append(type, selectedTypes))
      : without(type, selectedTypes);
    handleUpdateFacets(
      isAdmin
        ? [
            {
              name: "type",
              queryParam: "_type",
              terms: map(
                t => ({ code: t }),
                isEmpty(types) ? ALL_TYPES : types,
              ),
            },
          ]
        : [
            {
              name: "type",
              queryParam: "_type",
              terms: map(
                t => ({ code: t }),
                isEmpty(types) ? ALL_TYPES : types,
              ),
            },
            {
              name: "creatorOrgs",
              queryParam: "_co",
              terms: [{ code: currentOrgId }],
            },
          ],
    );
  };

  const searchResults = useSearch(state, isAdmin);

  const handlePageChange = page => {
    console.log("Handling page change...");
    console.log(page);
    handleUpdatePage(page);
  };

  const handleDateRangeChange = useCallback(
    value => {
      console.log(`Search Date : ${value}`);
      if (notBlank(value)) {
        handleUpdateOtherParams([{ aa: `${value}|` }]);
      } else {
        handleUpdateOtherParams([]);
      }
    },
    [handleUpdateOtherParams],
  );

  const [open, setOpen] = useState(false);
  const handleNewContentGroupClick = () => setOpen(true);
  const handleDialogClose = () => setOpen(false);

  return (
    <Card classes={{ root: classes.root }}>
      <NewContentGroupDialog open={open} handleClose={handleDialogClose} />

      <Grid container justify="space-between" alignItems="center">
        <Grid item>
          <Typography variant="h4" component="span">
            {title || "My Content Groups"}
          </Typography>
        </Grid>

        <Grid item>
          <Grid container alignItems="center" spacing={5}>
            <Grid item>
              <Button
                size="small"
                classes={{
                  textSizeSmall: classes.addButton,
                  iconSizeSmall: classes.addButton,
                }}
                variant="text"
                color="primary"
                startIcon={<AddIcon fontSize="small" />}
                onClick={handleNewContentGroupClick}
              >
                {`New ${
                  anyTrue([isAdmin, isIapManager])
                    ? "Content Group"
                    : "Collection"
                }`}
              </Button>
            </Grid>
            <Grid item>
              <DateRangeSelect
                initialRange={range || dateRanges.all}
                onDateRangeChange={handleDateRangeChange}
              />
            </Grid>
            <Grid item>
              <ContentGroupTypeSelect
                selectedTypes={selectedTypes}
                handleTypeChange={handleTypeChange}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {searchResults.isSuccess ? (
        <Grid container spacing={4} justify="flex-start">
          <Grid item>
            <Box>
              <Typography
                variant="h1"
                component="div"
                classes={{ h1: classes.h1 }}
              >
                {searchResults.data.totalCount.toLocaleString()}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      ) : (
        <Box>
          <Typography
            variant="h1"
            component="span"
            classes={{ h1: classes.h1 }}
          >
            Loading...
          </Typography>
        </Box>
      )}

      {searchResults.isSuccess ? (
        <Box className={classes.contentGroupList}>
          {notBlank(searchResults.data.media) ? (
            <Grid container justify="center" spacing={isMd ? 5 : 3}>
              {map(
                contentGroup => (
                  <Grid item key={contentGroup.id}>
                    <Link to={getEditUrl(contentGroup)}>
                      <Box className={classes.contentGroupItem}>
                        <Box className={classes.contentGroupThumbnailWrapper}>
                          <Image
                            className={classes.contentGroupThumbnail}
                            height={
                              isLg
                                ? IMAGE_DIMENSONS.LG
                                : isMd
                                ? IMAGE_DIMENSONS.MD
                                : isCreatorAdminDashMd
                                ? IMAGE_DIMENSONS.CREATOR_ADMIN_DASHBOARD_MD
                                : IMAGE_DIMENSONS.XS
                            }
                            src={contentGroup.thumbnailUrl}
                            alt="thumbnail of content group"
                          />
                        </Box>

                        <Typography className={classes.mediaType}>
                          {contentGroup.mediaType}
                        </Typography>
                        <Tooltip
                          title={contentGroup.displayName || contentGroup.name}
                        >
                          <Typography
                            color="primary"
                            variant="body2"
                            noWrap
                            classes={{ body2: classes.body2 }}
                          >
                            {contentGroup.displayName || contentGroup.name}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </Link>
                  </Grid>
                ),
                searchResults.data.media,
              )}
            </Grid>
          ) : (
            <Box
              width={"100%"}
              height={"100%"}
              ml={"auto"}
              mr={"auto"}
              mt={12}
              textAlign="center"
            >
              <Typography
                component="div"
                variant="body1"
                classes={{ body1: classes.body1 }}
              >
                No content groups have been created in the specified date range.
              </Typography>
              <Button
                size="small"
                classes={{
                  textSizeSmall: classes.addButton,
                  iconSizeSmall: classes.addButton,
                }}
                variant="text"
                color="primary"
                startIcon={<AddIcon fontSize="small" />}
                onClick={handleNewContentGroupClick}
              >
                New Content Group
              </Button>
            </Box>
          )}

          <Box mt={isLg ? 7 : isMd ? 10 : isCreatorAdminDashMd ? 8 : 10}>
            <DotPagination
              totalCount={searchResults.data.totalCount}
              page={state.page}
              onPageSelect={handlePageChange}
            />
          </Box>
        </Box>
      ) : (
        <Box className={classes.progressWrapper}>
          <PanelLoadingIndicator />
        </Box>
      )}
    </Card>
  );
};

export default ContentGroupsCard;
